import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Tab,
  Tabs,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ModuleList from "./ModuleList";
import ModuleDetails from "./ModuleDetails";
import DocumentManagerPanel from "./document_manager/DocumentManagerPanel";
import BackupsPanel from "./backups/BackupsPanel";

export default function DocsPage({ focusDocument = null }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [tab, setTab] = useState(focusDocument?.slug ? "documents" : "codebase");
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modulesError, setModulesError] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState("");

  useEffect(() => {
    const loadProjects = async () => {
      setProjectsLoading(true);
      setProjectsError(null);
      try {
        const res = await fetch("/api/docs/projects");
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || "Failed to load document projects.");
        }
        const nextProjects = Array.isArray(data.projects) ? data.projects : [];
        setProjects(nextProjects);
        setSelectedProjectId((prev) => {
          if (prev && nextProjects.some((project) => String(project?.project_id || "") === String(prev))) {
            return prev;
          }
          const requested = nextProjects.find((item) => String(item?.project_id || "") === String(focusDocument?.projectId || ""));
          if (requested) return String(requested.project_id);
          const runningApp = nextProjects.find((item) => String(item?.project_id || "") === "running_app");
          return String(runningApp?.project_id || nextProjects[0]?.project_id || "");
        });
      } catch (err) {
        setProjectsError(err.message || "Failed to load document projects.");
      } finally {
        setProjectsLoading(false);
      }
    };

    loadProjects();
  }, []);

  useEffect(() => {
    if (!focusDocument?.slug) return;
    setTab("documents");
    if (projects.some((item) => String(item.project_id) === String(focusDocument.projectId))) {
      setSelectedProjectId(String(focusDocument.projectId));
    }
  }, [focusDocument?.requestId, focusDocument?.slug, focusDocument?.projectId, projects]);

  useEffect(() => {
    if (tab !== "codebase") {
      return;
    }

    const loadModules = async () => {
      setModulesLoading(true);
      setModulesError(null);
      try {
        const params = new URLSearchParams();
        if (selectedProjectId) {
          params.set("project_id", selectedProjectId);
        }
        const res = await fetch(`/api/docs/modules${params.toString() ? `?${params.toString()}` : ""}`);
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || "Failed to load modules.");
        }
        const nextModules = Array.isArray(data.modules) ? data.modules : [];
        setModules(nextModules);
        setSelectedModule((prev) => {
          if (prev && nextModules.some((item) => String(item?.module || "") === String(prev))) {
            return prev;
          }
          return nextModules[0]?.module || null;
        });
      } catch (err) {
        setModulesError(err.message || "Failed to load modules.");
        setModules([]);
        setSelectedModule(null);
      } finally {
        setModulesLoading(false);
      }
    };

    loadModules();
  }, [selectedProjectId, tab]);

  const selectedProject = useMemo(
    () => projects.find((project) => String(project?.project_id || "") === String(selectedProjectId)) || null,
    [projects, selectedProjectId]
  );

  const showSharedProjectSelector = tab === "codebase" || tab === "documents";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box sx={{ px: { xs: 1, md: 2 }, pt: { xs: 1, md: 2 } }}>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab value="codebase" label="Codebase Docs" />
          <Tab value="documents" label="Document Manager" />
          <Tab value="backups" label="Backups" />
        </Tabs>
      </Box>

      {showSharedProjectSelector && (
        <Box sx={{ px: { xs: 1, md: 2 }, pb: 1 }}>
          <Paper variant="outlined" sx={{ p: { xs: 1.25, md: 1.5 } }}>
            <Stack spacing={1}>
              <FormControl size="small" fullWidth>
                <InputLabel id="docs-project-selector-label">Project</InputLabel>
                <Select
                  labelId="docs-project-selector-label"
                  label="Project"
                  value={selectedProjectId}
                  onChange={(event) => setSelectedProjectId(event.target.value)}
                  disabled={projectsLoading}
                >
                  {projects.map((project) => (
                    <MenuItem key={project.project_id} value={project.project_id}>
                      {project.label || project.project_id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color={projectsError ? "error" : "text.secondary"}>
                {projectsError || (projectsLoading
                  ? "Loading projects..."
                  : selectedProject?.doc_root_path
                    ? `Documents path: ${selectedProject.doc_root_path}`
                    : "Documents are scoped to the selected project.")}
              </Typography>
            </Stack>
          </Paper>
        </Box>
      )}

      {tab === "codebase" ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            height: "100%",
            minHeight: 0,
            p: { xs: 1, md: 2 },
            pt: 0,
          }}
        >
          <Paper
            elevation={2}
            sx={{
              width: { xs: "100%", md: 280 },
              minWidth: 0,
              maxHeight: { xs: "none", md: "100%" },
              overflow: "auto",
              p: { xs: 1.5, md: 2 },
            }}
          >
            <Typography variant="h6" gutterBottom>
              Modules
            </Typography>

            {modulesLoading && <CircularProgress size={24} />}

            {modulesError && (
              <Typography color="error" variant="body2">
                {modulesError}
              </Typography>
            )}

            {!modulesLoading && !modulesError && (
              <ModuleList
                modules={modules}
                selectedModule={selectedModule}
                onSelect={setSelectedModule}
              />
            )}
          </Paper>

          <Paper
            elevation={2}
            sx={{
              flex: 1,
              minWidth: 0,
              maxHeight: { xs: "none", md: "100%" },
              overflow: "auto",
              p: { xs: 1.5, md: 2 },
            }}
          >
            <ModuleDetails moduleName={selectedModule} selectedProjectId={selectedProjectId} isMobile={isMobile} />
          </Paper>
        </Box>
      ) : tab === "documents" ? (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DocumentManagerPanel focusDocument={focusDocument} selectedProjectId={selectedProjectId} projectOptions={projects} projectError={projectsError || ""} projectsLoading={projectsLoading} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <BackupsPanel />
        </Box>
      )}
    </Box>
  );
}
